'use client'

import React, { useState, useEffect, useRef, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { User, Bike, X, Phone, LogOut, Shield } from 'lucide-react'
import axios from 'axios'
import { API, useAuth } from '../context/AuthContext'
import { useBike } from '../context/BikeContext'
import { useAuthModal } from '../context/AuthModalContext'
import { toast } from 'sonner'
import { initializeGoogleSignIn, renderGoogleButton, isGoogleReady, setGoogleCallback } from '../utils/googleSignIn'

export default function AuthForm({ isOpen, onClose, initialView = 'login' }) {
  const { login, register, logout, googleLogin, refreshUser, token, isAuthenticated, user } = useAuth()
  const { selectedBike } = useBike()
  const { isRequired } = useAuthModal()
  const googleBtnRef = useRef(null)

  const [view, setView] = useState(initialView)
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({ mobile: '', password: '', name: '' })
  const [profile, setProfile] = useState(null)

  const needsMobile = profile?.mobile?.startsWith('g_')
  const hasRealMobile = user?.mobile && !user.mobile.startsWith('g_')

  const handleGoogleCallback = useCallback(async (response) => {
    setLoading(true)
    try {
      const loggedUser = await googleLogin(response.credential)
      toast.success('Welcome!')
      setProfile(loggedUser)
      setView('account')
    } catch {
      toast.error('Google login failed')
    } finally {
      setLoading(false)
    }
  }, [googleLogin])

  // Stable ref for callback to avoid re-initialization
  const callbackRef = useRef(null)
  
  useEffect(() => {
    callbackRef.current = handleGoogleCallback
  }, [handleGoogleCallback])

  useEffect(() => {
    if (!isOpen) return
    if (isAuthenticated) {
      fetchProfile()
      // Only show account mode if user has real mobile, otherwise show mobile prompt
      if (!hasRealMobile && initialView === 'account') {
        setView('account') // Will show mobile prompt due to needsMobile
      } else if (hasRealMobile && initialView === 'mobile') {
        // If user already has mobile and we tried to show mobile mode, close the modal
        onClose?.()
      } else {
        setView('account')
      }
    } else {
      setView(initialView === 'account' ? 'login' : initialView)
    }
  }, [isOpen, isAuthenticated, initialView, hasRealMobile, onClose])

  // Initialize Google Sign-In once globally when component first mounts
  useEffect(() => {
    if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) return

    initializeGoogleSignIn(process.env.REACT_APP_GOOGLE_CLIENT_ID, (response) => {
      // Use the latest callback from ref
      callbackRef.current(response)
    })
      .catch(err => {
        console.error('Google Sign-In initialization failed:', err)
      })
  }, []) // Empty deps - only run once

  // Update callback when handleGoogleCallback changes
  useEffect(() => {
    setGoogleCallback((response) => {
      callbackRef.current(response)
    })
  }, [handleGoogleCallback])

  // Render Google button when modal opens and ref is available
  useEffect(() => {
    if (!isOpen || !googleBtnRef.current || !process.env.REACT_APP_GOOGLE_CLIENT_ID) return

    // Wait for Google to be ready, then render button
    const renderButton = () => {
      if (googleBtnRef.current && isGoogleReady()) {
        renderGoogleButton(googleBtnRef.current)
      }
    }

    if (isGoogleReady()) {
      renderButton()
    } else {
      // If not ready yet, wait a bit and try again
      const timeout = setTimeout(() => {
        renderButton()
      }, 500)
      return () => clearTimeout(timeout)
    }
  }, [isOpen])

  const fetchProfile = async (authToken = token) => {
    if (!authToken) return
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      setProfile(res.data)
    } catch (err) {
      console.error(err)
    }
  }

  const handleLogin = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      const loggedUser = await login(formData.mobile, formData.password)
      toast.success('Login successful')
      setProfile(loggedUser)
      setView('account')
    } catch {
      toast.error('Invalid credentials')
    } finally {
      setLoading(false)
    }
  }

  const handleRegister = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      const registeredUser = await register(formData.mobile, formData.password, formData.name)
      toast.success('Account created!')
      setProfile(registeredUser)
      setView('account')
    } catch {
      toast.error('Registration failed')
    } finally {
      setLoading(false)
    }
  }

  const handleProfileUpdate = async () => {
    try {
      setLoading(true)
      const payload = { name: profile.name }

      // Always include mobile if it's set and not a temporary Google mobile
      if (profile.mobile && !profile.mobile.startsWith('g_')) {
        payload.mobile = profile.mobile
      }

      // Include phone if it's set
      if (profile.phone) {
        payload.phone = profile.phone
      }

      // For Google users requiring mobile, send the mobile number
      if (needsMobile && profile.mobile) {
        payload.mobile = profile.mobile
      }

      await axios.put(
        `${API}/users/me`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      )

      // Refresh user data from context
      const updated = await refreshUser()
      setProfile(updated)

      // Also fetch fresh profile data
      await fetchProfile()

      toast.success('Profile updated')
      // Close modal after successful update
      onClose?.()
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update failed')
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = () => {
    logout()
    setView('login')
    setProfile(null)
    onClose?.()
    toast.success('Logged out')
  }

  if (!isOpen) return null

  const inputClass =
    'w-full px-4 py-3 bg-[#101010] border border-white/15 text-white rounded-lg placeholder:text-gray-500 focus:outline-none focus:border-[#ffcc32] transition font-[Cormorant_Garamond] text-lg'

  const Container = (
    <div className="relative w-full max-w-md sm:max-w-lg bg-[#060606] border border-white/15 rounded-lg shadow-2xl shadow-black overflow-hidden max-h-[90vh] overflow-y-auto">
      {/* Header strip */}
      <div className="h-1 bg-gradient-to-r from-[#ffcc32] via-[#ff2b20] to-[#ffcc32]" />

      <div className="p-6 sm:p-8">
        {(!isRequired || !needsMobile) && (
          <button
            onClick={() => {
              if (!needsMobile) onClose()
            }}
            disabled={needsMobile && isRequired}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/10 text-gray-400 hover:text-white hover:bg-white/20 transition disabled:opacity-30"
          >
            <X size={18} />
          </button>
        )}

        {/* LOGIN / REGISTER */}
        {(view === 'login' || view === 'register') && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <h2
              className="text-4xl sm:text-5xl font-black text-white uppercase mb-1"
              style={{ fontFamily: 'Cinzel, serif', letterSpacing: "0.1em" }}
            >
              Rider Login
            </h2>
            <p className="text-zinc-400 text-lg mb-6 font-[Cormorant_Garamond]">
              Continue with Google. Your mobile number is required after login.
            </p>

            {process.env.REACT_APP_GOOGLE_CLIENT_ID && (
              <div className="mt-5 rounded-lg border border-white/10 bg-white p-3">
                <div ref={googleBtnRef} className="flex justify-center" />
              </div>
            )}

            {!process.env.REACT_APP_GOOGLE_CLIENT_ID && (
              <p className="text-sm text-red-300 mt-5">
                Google login is not configured. Add REACT_APP_GOOGLE_CLIENT_ID to enable login.
              </p>
            )}
            <p className="text-sm text-center text-gray-500 mt-5 font-[Cormorant_Garamond]">
              Secure Google sign-in for login and registration.
            </p>
          </motion.div>
        )}

        {/* ACCOUNT / PROFILE */}
        {view === 'account' && profile && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
            {/* Profile header */}
            {needsMobile && (
              <div className="rounded-lg border border-[#ffcc32]/50 bg-[#ffcc32]/10 p-4 text-[#ffefb0]">
                Mobile number is compulsory. Add your active mobile number to continue.
              </div>
            )}
            <div className="flex items-center gap-4 pb-5 border-b border-white/10">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#ffcc32] to-[#ff2b20] flex items-center justify-center text-black text-2xl font-black shrink-0">
                {(profile.name || profile.mobile || 'U').charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <h2 className="text-xl font-bold text-white truncate font-[Cormorant_Garamond]">
                  {profile.name || 'Rider'}
                </h2>
                <p className="text-zinc-400 text-sm truncate">{needsMobile ? 'Mobile required' : profile.mobile}</p>
                <span className="inline-flex items-center gap-1 mt-1 text-xs text-[#ffcc32] bg-[#ffcc32]/10 px-2 py-0.5 rounded-full">
                  <Shield size={10} /> Premium Member
                </span>
              </div>
            </div>

            {/* Profile fields */}
            <div>
              <h3 className="text-white font-bold flex items-center gap-2 mb-3 text-sm uppercase tracking-wider">
                <User size={14} className="text-emerald-500" /> Profile
              </h3>
              <div className="space-y-2">
                <input
                  value={profile.name || ''}
                  onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                  placeholder="Full Name"
                  className={inputClass}
                />
                <div className="relative">
                  <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                  <input
                    value={needsMobile ? profile.mobile.replace(/^g_.*/, '') : (profile.mobile || profile.phone || '')}
                    onChange={(e) => setProfile({ ...profile, mobile: e.target.value })}
                    placeholder="Mobile number"
                    className={`${inputClass} pl-10`}
                    required={needsMobile}
                  />
                </div>
              </div>
              <button
                onClick={handleProfileUpdate}
                disabled={loading}
                className="mt-3 w-full py-3 bg-[#ffcc32] hover:bg-[#ffda5c] text-black font-black rounded-lg transition text-lg font-[Cormorant_Garamond]"
              >
                {needsMobile ? 'Save Mobile Number' : 'Save Profile'}
              </button>
            </div>

            {/* Bike */}
            {selectedBike && (
              <div className="bg-[#0a1a0f] p-4 rounded-xl border border-emerald-700/30">
                <h3 className="text-white font-bold flex items-center gap-2 mb-2 text-sm">
                  <Bike size={14} className="text-orange-500" /> Selected Bike
                </h3>
                <p className="text-emerald-400 font-[Cormorant_Garamond]">
                  {selectedBike.brand} {selectedBike.model} {selectedBike.variant}
                </p>
              </div>
            )}

            <button
              onClick={handleLogout}
              className="w-full py-3 bg-red-500/90 hover:bg-red-500 text-white font-bold rounded-xl transition flex items-center justify-center gap-2 font-[Cormorant_Garamond]"
            >
              <LogOut size={16} /> Logout
            </button>
          </motion.div>
        )}
      </div>
    </div>
  )

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center px-4 py-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => {
            // Allow closing unless it's required for mobile number during checkout
            if (!(isRequired && needsMobile)) onClose()
          }}
        >
          <motion.div
            initial={{ scale: 0.92, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.92, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            {Container}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
